import { useCallback, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useTranslations } from '@/i18n';
import { cn } from '@/lib/utils';
import { useToggleHotelBooking } from '@/hooks/trips/useToggleHotelBooking';
import { useToggleTransportationBooking } from '@/hooks/trips/useToggleTransportationBooking';
import type {
  TripDetailCity,
  TripCityTransportation,
  TransportationAssistanceType,
} from '@/hooks/trips/types';

const AMOUNT_COMPARISON_THRESHOLD = 0.01;

function parseAmountInput(value: string | undefined): number | undefined {
  if (value == null) {
    return undefined;
  }

  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return undefined;
  }

  const normalized = trimmed.replace(/,/g, '.');
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }

  return parsed;
}

interface TripBookingChecklistProps {
  tripId: string;
  cities: TripDetailCity[];
  formatCurrency: (amount?: string | number) => string;
}

function safeFormatDate(value: string): string {
  if (!value) {
    return '';
  }

  try {
    return format(parseISO(value), 'dd/MM/yyyy');
  } catch {
    return value;
  }
}

export function TripBookingChecklist({
  tripId,
  cities,
  formatCurrency,
}: TripBookingChecklistProps) {
  const t = useTranslations('tripBooking');
  const tExpenses = useTranslations('expenses');

  const hotelMutation = useToggleHotelBooking();
  const transportationMutation = useToggleTransportationBooking();

  const [transportationAmounts, setTransportationAmounts] = useState<Record<number, string>>({});
  const transportAssistanceLabels = useMemo<Record<TransportationAssistanceType, string>>(
    () => ({
      ticket_or_advance: tExpenses('transportOptions.ticketOrAdvance'),
      rental_car: tExpenses('transportOptions.rentalCar'),
      car_with_driver: tExpenses('transportOptions.carWithDriver'),
    }),
    [tExpenses]
  );

  const resetTransportationAmount = useCallback((transportationId: number) => {
    setTransportationAmounts((previous) => {
      if (!(transportationId in previous)) {
        return previous;
      }

      const next = { ...previous };
      delete next[transportationId];
      return next;
    });
  }, []);

  const updateTransportationAmountDraft = useCallback(
    (transportation: TripCityTransportation, value: string) => {
      const fallback = transportation.amount != null ? String(transportation.amount) : '';

      setTransportationAmounts((previous) => {
        if (value === fallback) {
          if (!(transportation.id in previous)) {
            return previous;
          }

          const next = { ...previous };
          delete next[transportation.id];
          return next;
        }

        if (previous[transportation.id] === value) {
          return previous;
        }

        return {
          ...previous,
          [transportation.id]: value,
        };
      });
    },
    []
  );

  const handleTransportationAmountBlur = useCallback(
    (cityId: number, transportation: TripCityTransportation, draftValue: string) => {
      if (transportationMutation.isPending) {
        return;
      }

      const parsedAmount = parseAmountInput(draftValue);

      if (parsedAmount === undefined) {
        return;
      }

      const previousAmount = transportation.amount;

      if (
        typeof previousAmount === 'number' &&
        Math.abs(previousAmount - parsedAmount) < AMOUNT_COMPARISON_THRESHOLD
      ) {
        resetTransportationAmount(transportation.id);
        return;
      }

      transportationMutation.mutate(
        {
          tripId,
          cityId,
          transportationId: transportation.id,
          isBooked: transportation.isBooked,
          amount: parsedAmount,
        },
        {
          onSuccess: () => {
            resetTransportationAmount(transportation.id);
          },
        }
      );
    },
    [transportationMutation, tripId, resetTransportationAmount]
  );

  const bookingCities = useMemo(() => {
    return cities
      .map((city) => {
        const hotelsToBook = (city.hotels ?? []).filter((hotel) => hotel.bookingSupport);
        const transportationsToBook = (city.transportations ?? []).filter(
          (transportation) => transportation.bookingSupport
        );

        if (hotelsToBook.length === 0 && transportationsToBook.length === 0) {
          return null;
        }

        return {
          id: city.id,
          cityName: city.cityName,
          countryName: city.countryName,
          arrivalDate: city.arrivalDate,
          departureDate: city.departureDate,
          hotels: hotelsToBook,
          transportations: transportationsToBook,
        };
      })
      .filter((value): value is NonNullable<typeof value> => Boolean(value));
  }, [cities]);

  if (bookingCities.length === 0) {
    return null;
  }

  const isHotelUpdatingId =
    hotelMutation.isPending && hotelMutation.variables ? hotelMutation.variables.hotelId : null;
  const isTransportationUpdatingId =
    transportationMutation.isPending && transportationMutation.variables
      ? transportationMutation.variables.transportationId
      : null;

  return (
    <section className="mb-8">
      <div className="rounded-3xl bg-white px-4 py-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:px-8 md:py-7">
        <header className="mb-6">
          <h2 className="text-[1.5rem] leading-9 font-medium text-[#292929]">{t('title')}</h2>
        </header>

        <div className="flex flex-col gap-6">
          {bookingCities.map((city) => (
            <div
              key={city.id}
              className="rounded-[1.5rem] border border-[#f1f1f1] bg-[#f9f9f9] px-4 py-5 md:px-8"
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <h3 className="text-[1.375rem] leading-8 font-semibold text-[#292929]">
                  {t('cityHeading', {
                    city: city.cityName,
                    country: city.countryName,
                  })}
                </h3>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
                {city.transportations.map((transportation) => {
                  const amountValue =
                    transportationAmounts[transportation.id] ??
                    (transportation.amount != null ? String(transportation.amount) : '');
                  const assistanceKey = transportation.assistanceType ?? 'ticket_or_advance';
                  const assistanceLabel =
                    transportAssistanceLabels[assistanceKey] ?? t('transportTitle');
                  const requiresAmount = assistanceKey === 'ticket_or_advance';
                  const parsedDraftAmount = requiresAmount
                    ? parseAmountInput(amountValue)
                    : undefined;
                  const isAmountInvalid =
                    requiresAmount &&
                    (amountValue.trim().length === 0 || parsedDraftAmount === undefined);
                  const isLoading = isTransportationUpdatingId === transportation.id;
                  const transportDateRange =
                    city.arrivalDate && city.departureDate
                      ? `${safeFormatDate(city.arrivalDate)} - ${safeFormatDate(city.departureDate)}`
                      : null;

                  return (
                    <article
                      key={`transport-${transportation.id}`}
                      className={cn(
                        'flex h-full flex-col justify-between rounded-2xl border bg-white p-5 transition-colors',
                        transportation.isBooked ? 'border-[#ebebeb]' : 'border-[#E5484D]'
                      )}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
                            <img src="/assets/icons/plane.svg" alt="" className="h-6 w-6" />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex min-w-0 flex-col gap-1">
                              <span className="text-2xl leading-8 font-medium text-[#292929]">
                                {assistanceLabel}
                              </span>
                              {transportDateRange ? (
                                <span className="text-[15px] leading-6 text-[#666666]">
                                  {transportDateRange}
                                </span>
                              ) : null}
                            </div>
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                              {requiresAmount ? (
                                <>
                                  <div className="flex items-center gap-2">
                                    <span className="text-2xl leading-8 font-medium text-[#292929]">
                                      ₾
                                    </span>
                                    <Input
                                      value={amountValue}
                                      onChange={(event) =>
                                        updateTransportationAmountDraft(
                                          transportation,
                                          event.target.value
                                        )
                                      }
                                      onBlur={(event) =>
                                        handleTransportationAmountBlur(
                                          city.id,
                                          transportation,
                                          event.target.value
                                        )
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          handleTransportationAmountBlur(
                                            city.id,
                                            transportation,
                                            event.currentTarget.value
                                          );
                                        }
                                      }}
                                      disabled={isLoading}
                                      className={cn(
                                        'h-10 !w-auto rounded-[0.75rem] border border-[#171716] bg-white px-3 text-center text-2xl font-medium text-[#292929]',
                                        'focus-visible:border-[#171716] focus-visible:ring-0',
                                        isAmountInvalid ? 'border-[#e5484d]' : 'border-[#171716]'
                                      )}
                                      size={Math.min(10, Math.max(4, amountValue.length || 4))}
                                      aria-invalid={isAmountInvalid}
                                      aria-label={t('transportAmountInputLabel')}
                                      placeholder={t('transportAmountPlaceholder')}
                                      inputMode="decimal"
                                      autoComplete="off"
                                    />
                                  </div>
                                  {isLoading ? (
                                    <Spinner
                                      variant="circle"
                                      size={18}
                                      strokeWidth={2}
                                      className="text-black"
                                    />
                                  ) : null}
                                </>
                              ) : (
                                <div className="flex items-center gap-2 text-sm text-[#666666]">
                                  <span>{tExpenses('transportNoAmount')}</span>
                                  {isLoading ? (
                                    <Spinner
                                      variant="circle"
                                      size={18}
                                      strokeWidth={2}
                                      className="text-black"
                                    />
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <label
                          htmlFor={`transport-booked-${transportation.id}`}
                          className="flex min-w-0 items-center gap-3 text-[16px] leading-6 text-[#292929]"
                        >
                          <Checkbox
                            id={`transport-booked-${transportation.id}`}
                            checked={transportation.isBooked}
                            disabled={isLoading}
                            onCheckedChange={(checked) => {
                              const nextValue = checked === true;
                              const amountForRequest = requiresAmount
                                ? (parsedDraftAmount ?? transportation.amount)
                                : undefined;

                              if (requiresAmount && nextValue && amountForRequest === undefined) {
                                return;
                              }

                              transportationMutation.mutate(
                                {
                                  tripId,
                                  cityId: city.id,
                                  transportationId: transportation.id,
                                  isBooked: nextValue,
                                  amount: amountForRequest,
                                },
                                {
                                  onSuccess: () => {
                                    if (parsedDraftAmount !== undefined) {
                                      resetTransportationAmount(transportation.id);
                                    }
                                  },
                                }
                              );
                            }}
                            className="size-6 border-[#bbbbbb] data-[state=checked]:border-[#242424] data-[state=checked]:bg-[#242424]"
                          />
                          <span className="min-w-0 flex-1">{t('transportBookedLabel')}</span>
                        </label>
                        {isLoading ? (
                          <Spinner
                            variant="circle"
                            size={18}
                            strokeWidth={2}
                            className="text-black"
                          />
                        ) : null}
                      </div>
                    </article>
                  );
                })}

                {city.hotels.map((hotel) => {
                  const amountLabel =
                    hotel.totalAmount != null
                      ? formatCurrency(hotel.totalAmount)
                      : formatCurrency();
                  const subtitle =
                    hotel.checkInDate && hotel.checkOutDate
                      ? `${safeFormatDate(hotel.checkInDate)} - ${safeFormatDate(hotel.checkOutDate)}`
                      : t('hotelFallback');
                  const isLoading = isHotelUpdatingId === hotel.id;

                  return (
                    <article
                      key={`hotel-${hotel.id}`}
                      className={cn(
                        'flex h-full flex-col justify-between rounded-2xl border bg-white p-5 transition-colors',
                        hotel.isBooked ? 'border-[#ebebeb]' : 'border-[#E5484D]'
                      )}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
                            <img src="/assets/icons/building.svg" alt="" className="h-6 w-6" />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex min-w-0 flex-col gap-1">
                              <span className="text-2xl leading-8 font-medium text-[#292929]">
                                {t('hotelTitle')}
                              </span>
                              {subtitle ? (
                                <span className="text-[15px] leading-6 text-[#666666]">
                                  {subtitle}
                                </span>
                              ) : null}
                            </div>
                            <span className="self-start text-left text-2xl leading-8 font-medium text-[#292929] sm:self-auto sm:text-right">
                              {amountLabel}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <label
                          htmlFor={`hotel-booked-${hotel.id}`}
                          className="flex min-w-0 flex-1 items-center gap-3 text-[16px] leading-6 text-[#292929]"
                        >
                          <Checkbox
                            id={`hotel-booked-${hotel.id}`}
                            checked={hotel.isBooked}
                            disabled={isLoading}
                            onCheckedChange={(checked) => {
                              const nextValue = checked === true;
                              hotelMutation.mutate({
                                tripId,
                                cityId: city.id,
                                hotelId: hotel.id,
                                isBooked: nextValue,
                              });
                            }}
                            className="size-6 border-[#bbbbbb] data-[state=checked]:border-[#242424] data-[state=checked]:bg-[#242424]"
                          />
                          <span className="min-w-0 flex-1">{t('hotelBookedLabel')}</span>
                        </label>
                        {isLoading ? (
                          <Spinner
                            variant="circle"
                            size={18}
                            strokeWidth={2}
                            className="text-black"
                          />
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TripBookingChecklist;
